import React, {useCallback, useEffect, useRef, useState} from "react";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import braintree from "braintree-web";
import {isoLocales} from "../PaymentController";
import {getShopId} from "../../getShopId";
import GooglePayButton, {ReadyToPayChangeResponse} from "@google-pay/button-react";
import {Checkout} from "../../../../typescript-library";
import {
  handleShippingAddress,
  initialize,
  positionsToDisplayItems,
  prepareError,
  preparePaymentDataRequest,
  setNewShippingOption,
} from "./googlePayHelperFunctions";
import {ButtonPropType} from "../paypal/helpers/paypalTypes";
import {useTranslation} from "react-i18next";
import {useCustomerSelector} from "../../../../store";
import {GPayAuthError, onGoogleAuthorized} from "./onGoogleAuthorized";
import ErrorBoundary from "../../../utils/ErrorBoundary";

const environment = __PRODUCTION__ ? "PRODUCTION" : "TEST";
const shippingOptionUnselected = "shipping_option_unselected";
const gPayButtonStyleNotReady: React.CSSProperties = {display: "none"};
const gPayButtonStyle: React.CSSProperties = {display: "block", height: "40px", marginTop: ".5rem"};
const blockedOpacity = ".38";

const GooglePayButtonLoader: React.FC<ButtonPropType> = ({handleError, isBlocked, onReturn}) => {
  const {t} = useTranslation();
  const paymentRequestRef = useRef<google.payments.api.PaymentDataRequest | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<google.payments.api.PaymentDataRequest | null>(null);
  const initializingDoneRef = useRef<boolean>(false);
  const [buttonStyles, setButtonStyles] = useState<React.CSSProperties>(gPayButtonStyleNotReady);
  const [googlePayInstance, setGooglePayInstance] = useState<braintree.GooglePayment>();
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

  const {customer} = useCustomerSelector();
  const {cart, braintreeData, countries, setViewState, viewState} = useCartContext();
  const buttonRef = useRef<GooglePayButton | null>(null);
  const checkoutRef = useRef<Checkout>();

  paymentRequestRef.current = paymentRequest;

  useEffect(() => {
    if (!braintreeData) {
      return;
    }

    void preparePaymentDataRequest(braintreeData, cart, countries)
      .then(({paymentDataRequest, gPayInstance}) => {
        setGooglePayInstance(gPayInstance);
        paymentRequestRef.current = paymentDataRequest;
        setPaymentRequest(paymentRequestRef.current);
      })
      .catch((e) => {
        handleError(e as Error, "paymentRequest object preparation");
      });
  }, [braintreeData]);

  // When cart is open and gpay is closed, and user changes something. this comp still
  // exists, and these 'external' changes won't take effect on visualization in gpay
  useEffect(() => {
    if (isPopupOpen) {
      return;
    }
    setPaymentRequest((prevState) => {
      if (!prevState) {
        return prevState;
      }

      return {
        ...prevState,
        transactionInfo: {
          ...prevState.transactionInfo,
          totalPrice: cart.valuePayable.amount,
          displayItems: positionsToDisplayItems(cart),
          currencyCode: cart.currencyCode,
          countryCode: cart.countryCode,
        },
        shippingOptionParameters: undefined,
      };
    });
  }, [cart.valuePayable.amount, cart.positions, cart.currencyCode, cart.countryCode]);

  /**
   * handler for initialize call, and changes in shipping address, shipping option
   */
  const onPaymentDataChanged = useCallback(
    async (
      intermediatePaymentData: google.payments.api.IntermediatePaymentData,
    ): Promise<google.payments.api.PaymentDataRequestUpdate> => {
      if (!paymentRequestRef.current) {
        return {};
      }
      if (intermediatePaymentData.callbackTrigger === "INITIALIZE") {
        // Very first initialize call, to get shipping options and the necessity of a phone number
        // and a cart total calculation based on the most preferred shipping option
        const {isPhoneRequired, requestUpdate} = await initialize(
          cart,
          intermediatePaymentData,
          paymentRequestRef.current,
          checkoutRef,
        );
        initializingDoneRef.current = true;
        setNewPaymentRequest(requestUpdate, isPhoneRequired);

        return requestUpdate;
      }

      if (!checkoutRef.current) {
        return {};
      }

      if (intermediatePaymentData.callbackTrigger === "SHIPPING_OPTION" && intermediatePaymentData.shippingOptionData) {
        // Recalculate cart total, and see if a phone number is required
        const {isPhoneRequired, requestUpdate} = await setNewShippingOption(
          checkoutRef.current,
          paymentRequestRef.current,
          intermediatePaymentData.shippingOptionData,
        );
        await cart.get();
        setNewPaymentRequest(requestUpdate, isPhoneRequired);

        return requestUpdate;
      } else if (intermediatePaymentData.callbackTrigger === "SHIPPING_ADDRESS") {
        // See if country code changed, and if so, retrieve new shipping options
        const {isPhoneRequired, requestUpdate} = await handleShippingAddress(
          cart,
          checkoutRef.current,
          intermediatePaymentData,
          paymentRequestRef.current,
        );
        setNewPaymentRequest(requestUpdate, isPhoneRequired);

        return requestUpdate;
      }

      handleError(
        new GPayAuthError("No if block matches in 'onPaymentDataChanged'", "GPayInternalError"),
        "onPaymentDataChanged",
      );
      await checkoutRef.current?.put({paymentId: null});

      return {
        error: {
          intent: "PAYMENT_METHOD",
          reason: "OTHER_ERROR",
          message: "Something went wrong. Please try again",
        },
      };
    },
    [paymentRequest],
  );

  const onPaymentAuthorized = useCallback<google.payments.api.PaymentAuthorizedHandler>(
    async (paymentData: google.payments.api.PaymentData) => {
      const result: google.payments.api.PaymentAuthorizationResult = {
        transactionState: "SUCCESS",
      };
      if (!paymentData.shippingOptionData || paymentData.shippingOptionData.id === shippingOptionUnselected) {
        result.transactionState = "ERROR";
        if (initializingDoneRef.current) {
          // init has returned no shipping options for that country
          result.error = {
            intent: "PAYMENT_AUTHORIZATION",
            reason: "SHIPPING_ADDRESS_UNSERVICEABLE",
            message: t("checkout.noShippingToSelectedAddress"),
          };
        } else {
          // user continued too fast, and init hasn't returned yet
          result.error = {
            intent: "PAYMENT_AUTHORIZATION",
            reason: "SHIPPING_OPTION_INVALID",
            message: t("checkout.noValidShippingOption"),
          };
        }

        return result;
      }

      if (!googlePayInstance) {
        handleError(new GPayAuthError("No google pay instance"), "Google Pay AuTT", {paymentData});
        await checkoutRef.current?.put({paymentId: null});

        return {
          transactionState: "ERROR",
          error: {
            intent: "PAYMENT_AUTHORIZATION",
            reason: "PAYMENT_DATA_INVALID",
            message: t("general.emailNetworkError"),
          },
        };
      }

      try {
        await onGoogleAuthorized(paymentData, customer.email, checkoutRef.current);
        const tokenData = await googlePayInstance.parseResponse(paymentData);
        setViewState({
          ...viewState,
          gPayData: {
            tokenData,
            paymentData,
          },
          paypalInfos: undefined,
          appleData: undefined,
        });
        onReturn();
      } catch (e) {
        handleError(e as Error, "Google Pay AuTT");
        await checkoutRef.current?.put({paymentId: null});
        result.transactionState = "ERROR";
        result.error = {
          intent: "PAYMENT_AUTHORIZATION",
          reason: "PAYMENT_DATA_INVALID",
          message: t("general.emailNetworkError"),
        };
      }

      return result;
    },
    [customer, googlePayInstance],
  );

  if (!paymentRequest || !paymentRequestRef.current) {
    return null;
  }

  const setNewPaymentRequest = (
    requestUpdate: google.payments.api.PaymentDataRequestUpdate,
    isPhoneRequired?: boolean,
  ) => {
    setPaymentRequest((prevState) => {
      if (!prevState) return null;

      return {
        ...prevState,
        shippingAddressParameters: {
          ...prevState.shippingAddressParameters,
          phoneNumberRequired: isPhoneRequired,
        },
        shippingOptionParameters: requestUpdate.newShippingOptionParameters,
        transactionInfo: requestUpdate.newTransactionInfo ?? prevState.transactionInfo,
      };
    });
  };

  return (
    <GooglePayButton
      ref={buttonRef}
      environment={environment}
      buttonType="checkout"
      buttonLocale={isoLocales[getShopId()].locale.split("-")[0]}
      paymentRequest={paymentRequestRef.current}
      buttonSizeMode="fill"
      buttonColor="black"
      onReadyToPayChange={(param: ReadyToPayChangeResponse) => {
        // Not quite clear, what either one means, that's why that double check here
        // And only do a setState, when there's an actual change here
        if (param.isReadyToPay && param.isButtonVisible && buttonStyles.display === "none") {
          setButtonStyles(gPayButtonStyle);
        } else if (!(param.isReadyToPay && param.isButtonVisible) && buttonStyles.display === "block") {
          setButtonStyles(gPayButtonStyleNotReady);
        }
      }}
      onPaymentDataChanged={onPaymentDataChanged}
      onPaymentAuthorized={onPaymentAuthorized}
      onLoadPaymentData={(paymentData) => {
        if (__DEVELOPMENT__) {
          // eslint-disable-next-line no-console
          console.log("onLoadPaymentData:", paymentData);
        }
      }}
      onClick={(event) => {
        if (isBlocked) {
          event.preventDefault();
        } else {
          setIsPopupOpen(true);
        }
      }}
      onError={(err) => {
        setIsPopupOpen(false);
        initializingDoneRef.current = false;
        handleError(prepareError(err), "Google Pay");
      }}
      onCancel={(error) => {
        if (__DEVELOPMENT__) {
          // eslint-disable-next-line no-console
          console.log(error);
        }
        setIsPopupOpen(false);
        initializingDoneRef.current = false;
        if (checkoutRef.current?.paymentId) {
          void checkoutRef.current?.put({paymentId: null});
        }
      }}
      style={isBlocked ? {...buttonStyles, opacity: blockedOpacity} : buttonStyles}
    />
  );
};

const GPayButton: React.FC<ButtonPropType> = (props) => {
  return (
    <ErrorBoundary fallback={null} handleError={props.handleError} occurrence="GooglePay Error boundary">
      <GooglePayButtonLoader {...props} />
    </ErrorBoundary>
  );
};

export default GPayButton;
